<template>
  <div>
    <button @click="openModal" class="trigger-button">
      企业定制咨询
    </button>

    <Teleport to="body">
      <Transition name="modal">
        <div v-if="isOpen" class="modal-overlay">
          <div class="modal-container" @click.stop>
            <button @click="closeModal" class="close-button" aria-label="关闭">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>

            <Transition name="fade">
              <div v-if="isSubmitting" class="loading-overlay" aria-live="polite" aria-busy="true">
                <div class="spinner"></div>
                <div class="loading-text">正在提交，请稍候…</div>
              </div>
            </Transition>



            <div class="modal-content">
              <div class="form-section">
                <h2 class="form-title">企业定制咨询</h2>

                <form @submit.prevent="handleSubmit" class="survey-form">
                  <div class="form-group">
                    <label for="requirement_desc" class="form-label">请简要描述您的需求</label>
                    <textarea id="requirement_desc" v-model="formData.requirement_desc"
                      :class="['form-textarea', { 'error': errors.requirement_desc }]" placeholder="请输入您的需求描述..."
                      rows="4"></textarea>
                    <span v-if="errors.requirement_desc" class="error-message">{{ errors.requirement_desc }}</span>
                  </div>

                  <div class="form-group">
                    <label for="company_name" class="form-label">您所属的企业是？</label>
                    <input id="company_name" v-model="formData.company_name" type="text"
                      :class="['form-input', { 'error': errors.company_name }]" placeholder="请输入企业名称" />
                    <span v-if="errors.company_name" class="error-message">{{ errors.company_name }}</span>
                  </div>

                  <div class="form-group">
                    <label for="contact_name" class="form-label">怎么称呼您？</label>
                    <input id="contact_name" v-model="formData.contact_name" type="text"
                      :class="['form-input', { 'error': errors.contact_name }]" placeholder="请输入您的称呼" />
                    <span v-if="errors.contact_name" class="error-message">{{ errors.contact_name }}</span>
                  </div>

                  <div class="form-group">
                    <label for="phone" class="form-label">您的手机号是？</label>
                    <input id="phone" v-model="formData.phone" type="tel"
                      :class="['form-input', { 'error': errors.phone }]" placeholder="请输入您的手机号" />
                    <span v-if="errors.phone" class="error-message">{{ errors.phone }}</span>
                  </div>

                  <button type="submit" :disabled="isSubmitting || submittedDisabled" class="submit-button">
                    {{ isSubmitting ? '提交中...' : '提交按钮' }}
                  </button>
                </form>

                <Transition name="fade">
                  <div v-if="showSuccess" class="success-message">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd" />
                    </svg>
                    提交成功！
                  </div>
                </Transition>
              </div>

              <div class="qr-section">
                <h2 class="qr-title">扫码立即联系我们</h2>
                <div class="qr-code-container">
                  <img src="../../assets/images/web/limo_qrcode.png" alt="企业微信二维码" class="qr-code-image" />
                </div>
                <p class="qr-description">扫描二维码添加企业微信</p>
                <p class="qr-description">获取更快的响应</p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showGlobalToast" class="global-toast" role="status" aria-live="polite">
          {{ globalToastMessage }}
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { submitSurvey } from '../../utils/index.js'

const isOpen = ref(false)
const isSubmitting = ref(false)
const submittedDisabled = ref(false)
const showSuccess = ref(false)

const formData = reactive({
  requirement_desc: '',
  company_name: '',
  contact_name: '',
  phone: ''
})

const errors = reactive({
  requirement_desc: '',
  company_name: '',
  contact_name: '',
  phone: ''
})

const showGlobalToast = ref(false)
const globalToastMessage = ref('')
const triggerGlobalToast = (msg, duration = 5000) => {
  globalToastMessage.value = msg || ''
  showGlobalToast.value = true
  setTimeout(() => {
    showGlobalToast.value = false
    globalToastMessage.value = ''
  }, duration)
}

const STORAGE_KEY = 'survey_form_cache'
onMounted(() => {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached)
      formData.requirement_desc = parsed.requirement_desc || ''
      formData.company_name = parsed.company_name || ''
      formData.contact_name = parsed.contact_name || ''
      formData.phone = parsed.phone || ''
    }
  } catch (e) { }
})

watch(formData, (val) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(val))
  } catch (e) { }
}, { deep: true })

const resetForm = () => {
  formData.requirement_desc = ''
  formData.company_name = ''
  formData.contact_name = ''
  formData.phone = ''
  Object.keys(errors).forEach(key => errors[key] = '')
  showSuccess.value = false
  try { localStorage.removeItem(STORAGE_KEY) } catch (e) { }
}

const openModal = () => {
  isOpen.value = true
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  if (isSubmitting.value) return
  submittedDisabled.value = false
  resetForm()
  isOpen.value = false
  document.body.style.overflow = ''
}

const validateForm = () => {
  let isValid = true

  Object.keys(errors).forEach(key => errors[key] = '')

  if (!formData.company_name.trim()) {
    errors.company_name = '请输入企业名称'
    isValid = false
  }

  if (!formData.contact_name.trim()) {
    errors.contact_name = '请输入您的称呼'
    isValid = false
  }

  if (!formData.phone.trim()) {
    errors.phone = '请输入手机号码'
    isValid = false
  } else {
    const isCnMobile = /^1[3-9]\d{9}$/.test(formData.phone)
    if (!isCnMobile) {
      errors.phone = '请输入有效的中国大陆手机号'
      isValid = false
    }
  }

  return isValid
}

const handleSubmit = async () => {
  if (!validateForm()) return
  if (submittedDisabled.value) return

  isSubmitting.value = true

  try {
    const res = await submitSurvey(formData)
    if (res && typeof res.code !== 'undefined') {
      if (Number(res.code) === 1) {
        triggerGlobalToast(res.message || '提交失败', 3000)
        return
      }
    }
    isSubmitting.value = false
    submittedDisabled.value = true
    setTimeout(() => {
      closeModal()
    }, 3000)
    triggerGlobalToast('提交成功，我们会尽快联系您！', 3000)
  } catch (error) {
    console.error('提交失败:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.77);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
  overflow-y: auto;
}

.modal-container {
  background: #fff;
  border-radius: 34px;
  border: 3px solid #d0fad7;
  width: 100%;
  max-width: 1080px;
  overflow: visible;
  position: relative;
  box-shadow: 0 10px 48px 0 rgba(182, 227, 146, 0.19);
  padding: 0;
  transition: box-shadow 0.22s, transform 0.19s;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 254, 240, 0.79);
  color: #369579;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.16s, color 0.16s;
  z-index: 13;
  box-shadow: 0 2px 6px 0 rgba(145, 193, 74, 0.06);
}

.close-button:hover {
  background: #eaffd0;
  color: #fe7256;
}

.modal-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 48px 38px 34px 38px;
}

@media (min-width: 800px) {
  .modal-content {
    flex-direction: row;
    gap: 56px;
    padding: 58px 44px 36px 44px;
  }
}

.form-section {
  flex: 2;
  display: flex;
  flex-direction: column;
}

.form-title {
  font-size: 26px;
  font-weight: 900;
  color: #222;
  margin-bottom: 28px;
  text-align: left;
  letter-spacing: 1px;
}

.survey-form {
  display: flex;
  flex-direction: column;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-label {
  font-size: 15px;
  font-weight: 600;
  color: #2e2960;
}

.form-input,
.form-textarea {
  padding: 13px 18px;
  border: 2px solid #e8f9e0;
  border-radius: 11px;
  font-size: 15px;
  color: #333;
  box-shadow: 0 2px 6px 0 rgba(186, 246, 170, 0.06);
  font-family: inherit;
  background: #f8fefa;
  transition: background 0.16s, border-color 0.16s;
  resize: none
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #b8ee9b;
  background: #fff;
}

.form-input.error,
.form-textarea.error {
  border-color: #fe7256;
  background: #fff6f4;
}

.form-textarea {
  min-height: 95px;
}

.error-message {
  font-size: 12px;
  color: #fe7256;
  margin-top: -2px;
}

.submit-button {
  padding: 16px 0;
  background: linear-gradient(100deg, #ff595a 5%, #ff9273 55%, #ffba5a 100%);
  color: #fff;
  border: none;
  border-radius: 32px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 1.5px;
  cursor: pointer;
  margin-top: 15px;
  box-shadow: 0 5px 30px 0 rgba(241, 90, 90, 0.19);
  transition: box-shadow 0.2s, background 0.22s, transform 0.16s;
  text-shadow: 0 1px 8px rgba(255, 181, 145, 0.10);
  will-change: transform, box-shadow;
}

.submit-button:hover:not(:disabled) {
  transform: scale(1.015) translateY(-2.5px);
  background: linear-gradient(90deg, #fe7c68 4%, #ffb76b 82%);
  box-shadow: 0 14px 40px 0 rgba(255, 81, 81, 0.20), 0 4px 16px #fff7ef;
}

.submit-button:active {
  transform: scale(0.98);
}

.submit-button:disabled {
  opacity: 0.62;
  cursor: not-allowed;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 13px;
  background: #e8fcd1;
  color: #50a95c;
  border-radius: 13px;
  font-weight: 600;
  margin-top: 15px;
  font-size: 15px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 12;
  border-radius: 34px;
}

.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e8f9e0;
  border-top-color: #ff7f66;
  animation: spin 0.9s linear infinite;
  box-shadow: 0 2px 10px rgba(255, 121, 99, 0.12);
}

.loading-text {
  margin-top: 10px;
  font-size: 14px;
  color: #666;
  font-weight: 600;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.global-toast {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.86);
  color: #fff;
  padding: 14px 18px;
  border-radius: 13px;
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.22);
  z-index: 2000;
  text-align: center;
  max-width: 80vw;
  line-height: 1.4;
}

.qr-section {
  flex: 1.1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fafee0;
  border: 2.5px solid #d8fbad;
  border-radius: 18px;
  padding: 32px 15px 18px 15px;
  box-shadow: 0 2px 13px rgba(186, 246, 170, 0.10);
  place-self: stretch;
  min-width: 210px;
  margin-top: 0;
  margin-bottom: 0;
}

.qr-title {
  font-size: 18px;
  font-weight: 700;
  color: #369579;
  margin-bottom: 22px;
  text-align: center;
}

.qr-code-container {
  width: 288px;
  height: 288px;
  background: #fff;
  border-radius: 15px;
  padding: 14px;
  box-shadow: 0 4px 13px rgba(118, 229, 93, 0.09);
  margin-bottom: 10px;
  border: 1.8px solid #f1fad9;
}

.qr-code-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.qr-description {
  font-size: 14px;
  color: #7ab236;
  text-align: center;
  margin-top: 4px;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.27s cubic-bezier(.36, 1.25, .33, 1), transform 0.28s cubic-bezier(.42, 1.11, .39, 1);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.97);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.20s cubic-bezier(.39, .62, .41, 1.1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 900px) {
  .modal-container {
    border-radius: 16px;
    max-width: 98vw;
  }

  .modal-content {
    padding: 24px 5px 9px 5px;
    gap: 15px;
  }

  .qr-section {
    padding: 12px 2px 4px 2px;
    min-width: 0;
  }
}

@media (max-width: 480px) {
  .modal-container {
    box-shadow: 0 5px 14px rgba(153, 234, 122, 0.10);
    border-radius: 9px;
  }

  .close-button {
    top: 7px;
    right: 7px;
    width: 32px;
    height: 32px;
  }

  .modal-content {
    padding: 7px 0 3px 0;
    gap: 7px;
  }

  .qr-section {
    border-radius: 11px;
    padding: 7px 0 3px 0;
  }

  .submit-button {
    padding: 12px 0;
    font-size: 16px;
  }
}

.trigger-button {
  padding: 8px 16px;
  border: none;
  border-radius: 32px;
  background: linear-gradient(90deg, #ff595a 7%, #ffba5a 98%);
  color: #fff;
  font-size: 16px;
  font-weight: 800;
  box-shadow: 0 6px 30px rgba(255, 121, 99, 0.14);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.22s, background 0.18s, transform 0.17s;
  cursor: pointer;
  margin-left: 56px;
}

.trigger-button:hover {
  background: linear-gradient(90deg, #ff9273 9%, #ff595a 97%);
  box-shadow: 0 12px 38px rgba(255, 121, 99, 0.20);
  transform: scale(1.035);
}

.trigger-button:active {
  transform: scale(0.97);
}

@media (max-width: 600px) {
  .modal-container {
    max-width: 96vw;
    border-radius: 12px;
    min-width: unset;
  }

  .modal-content {
    flex-direction: column !important;
    padding: 14px 4vw 12px 4vw;
    gap: 12px;
    align-items: stretch;
  }

  .trigger-button {
    font-size: 14px;
    padding: 7px 10px;
    margin-left: 16px;
    min-width: unset;
    height: 38px;
    border-radius: 18px;
  }

  .form-title {
    font-size: 19px;
    margin-bottom: 18px;
  }

  .survey-form .form-label,
  .form-input,
  .form-textarea {
    font-size: 14px !important;
  }

  .qr-section {
    padding: 14px 3px 6px 3px !important;
    border-radius: 12px;
  }

  .qr-title {
    font-size: 15px;
  }

  .qr-code-container {
    width: 144px;
    height: 144px;
    padding: 7px;
    border-radius: 11px;
  }

  .qr-description {
    font-size: 12px;
    line-height: 1.3;
    margin-bottom: 2px;
  }
}

@media (max-width: 375px) {
  .modal-content {
    padding: 5px 2vw 5px 2vw;
  }

  .qr-code-container {
    width: 110px;
    height: 110px;
    padding: 2.5px;
  }
}
</style>